import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterOutlet, NavigationCancel, NavigationEnd, NavigationError } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import {
  AppInsightsService,
  AuthService,
  CFIErrorHandlerService,
  InactivityMonitorService,
  LoadingSpinnerService,
  NotificationService,
  SignalRService,
  ToastMessageService,
  isFullPageRoute,
  registerSharedIcons,
  resolveSignalRBase,
  SIGN_OUT_PATH
} from '@k12/app-services';
import {
  BreadcrumbsComponent,
  LibFooterComponent,
  LibHeaderComponent,
  LibSubheaderComponent
} from '@k12/app-components';
import { SideDrawerComponent, SideDrawerService } from '@k12/admin-components';
import { HeaderItem, TopMenuData, ToastMessageType } from '@k12/models';
import { Subject, takeUntil, forkJoin, from } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageCenterService } from './pages/message_center/message_center.service';
import { ProviderRecordService } from './services/provider-record.service';
import { SystemService } from './services/system.service';
import { AwardsService } from './services/awards/awards.service';
import { environment } from '../environments/environment';
import { adminStore } from './stores/admin-store';


@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-root',
  imports: [
    BreadcrumbsComponent,
    CommonModule,
    LibFooterComponent,
    LibHeaderComponent,
    LibSubheaderComponent,
    MatProgressSpinnerModule,
    MatSidenavModule,
    RouterOutlet,
    SideDrawerComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [CFIErrorHandlerService]
})
export class AppComponent implements OnDestroy, OnInit {
  public adminUrl: string = environment.adminApiUrl;
  public authService: AuthService = inject(AuthService);
  private readonly appInsightsService = inject(AppInsightsService);
  public domSanitizer: DomSanitizer = inject(DomSanitizer);
  public headerItems: HeaderItem[] = [];
  public isSuperAdmin: boolean = false;
  public matIconRegistry: MatIconRegistry = inject(MatIconRegistry);
  public menu: TopMenuData[] = [];
  public showLoadingSpinner: WritableSignal<boolean> = signal(false);
  sideDrawerService = inject(SideDrawerService);
  public systemService = inject(SystemService);
  public unreadMessageCount: number = 0;
  private readonly destroy$: Subject<void> = new Subject();
  private readonly inactivityMonitor = inject(InactivityMonitorService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly msalService = inject(MsalService);
  private readonly notificationService = inject(NotificationService);
  private readonly providerRecordService = inject(ProviderRecordService);
  private readonly messageCenterService: MessageCenterService = inject(MessageCenterService);
  private readonly signalRService = inject(SignalRService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly awardsService = inject(AwardsService);
  private readonly adminStore = inject(adminStore);
  private readonly router = inject(Router);
  private userName: string = '';

  // Routes that supply their own logo header, so portal chrome is suppressed
  public isFullPageRoute: WritableSignal<boolean> = signal(false);

  // The router outlet is empty from bootstrap until the first route component activates - guards,
  // resolvers and the lazy chunk download all happen in that window, so it starts out loading
  public isRouteLoading: WritableSignal<boolean> = signal(true);

  constructor() {
    registerSharedIcons(this.matIconRegistry, this.domSanitizer);

    this.applyChromeForUrl(window.location.pathname);

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.applyChromeForUrl(event.url);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        // Nothing will activate the outlet, so stop loading instead of spinning forever
        this.isRouteLoading.set(false);
      }
    });
  }

  public ngOnInit(): void {
    if (window.location.pathname !== SIGN_OUT_PATH) {
      this.showLoadingSpinner.set(true);
      this.inactivityMonitor.startTracking(60, 5);
      this.inactivityMonitor.isIdleWarning.subscribe({
        next: (res) => {
          if (res) {
            this.toastMessageService.showToast(ToastMessageType.Info, 'Your session is about to expire.');
          }
        },
        error: () => this.appInsightsService.logEvent('AppComponent.InactivityMonitor.StreamError')
      });
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.initAdmin(); // safe to load now
    } else {
      // Defer until login completes
      this.msalService.instance.addEventCallback(async (event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS) {
          const acct = this.msalService.instance.getAllAccounts()[0];
          if (acct) {
            this.msalService.instance.setActiveAccount(acct);
            this.initAdmin();
          }
        }
      });
    }

    // Subscribe to unreadMessageCount changes
    this.systemService.unreadMessageCount$.subscribe({
      next: (count) => {
        this.unreadMessageCount = count;
      },
      error: () => this.appInsightsService.logEvent('AppComponent.UnreadMessageCount.StreamError')
    });

    // Subscribe to global loading spinner updates
    this.loadingSpinnerService.loadingSpinner$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (showSpinner: boolean) => {
        this.showLoadingSpinner.set(showSpinner);
      },
      error: () => this.appInsightsService.logEvent('AppComponent.LoadingSpinner.StreamError')
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public logout(): void {
    this.systemService.logout();
  }

  public onRouteActivate(): void {
    this.isRouteLoading.set(false);
  }

  public onRouteDeactivate(): void {
    this.isRouteLoading.set(true);
  }

  private buildHeader(): void {
    this.headerItems = [
      {
        label: 'Search',
        icon: '../assets/images/search.svg',
        route: ''
      },
      {
        label: 'FAQ',
        icon: '../assets/images/faq.svg',
        route: 'https://k12.ncseaa.edu/the-education-student-accounts/current-families/faqs/',
        url: 'true'
      },
      {
        label: 'Alerts',
        icon: '../assets/images/alerts.svg',
        route: ''
      },
      {
        label: 'Messaging',
        icon: '../assets/images/messages.svg',
        route: !this.isSuperAdmin ? 'messages' : '',
        dropdown: this.isSuperAdmin
          ? [
              {
                label: 'Messages',
                route: 'messages'
              },
              {
                label: 'Assign Messages',
                route: 'messages/auto-assignment'
              }
            ]
          : undefined
      },
      {
        label: 'Profile',
        icon: '../assets/images/profile.svg',
        route: '',
        tooltip: this.userName,
        dropdown: [
          {
            label: this.userName,
            route: 'work-queue'
          },
          {
            label: 'Logout',
            cmd: true
          }
        ]
      }
    ];
  }
  private buildNavigation(): void {
    if (this.menu.length > 0) return;
    const workQueueText = 'Taskpad';
    const workQueueRoute = 'work-queue';
    const clipboardListIcon = 'assets/images/clipboard-list.svg';
    const clipboardUsersIcon = 'assets/images/clipboard-users.svg';
    const grid2Icon = 'assets/images/grid-2.svg';
    const communicationCenterRoute = 'communications-center';

    this.menu = [
      this.isSuperAdmin
      ? {
        icon: clipboardListIcon,
        label: workQueueText,
        dropdown: [
          { label: workQueueText, route: workQueueRoute },
          { label: 'Work Queue Assignments', route: 'work-queue/assignments' },
          { label: 'Tasks', route: 'tasks' },
          { label: 'Task Names', route: 'tasks/templates' }
        ],
        route: 'home',
        isHome: true
        }
      : {
        icon: clipboardListIcon,
        label: workQueueText,
        route: 'home',
        isHome: true,
        dropdown: [
          { label: workQueueText, route: workQueueRoute },
          { label: 'Tasks', route: 'tasks' }
        ]
        },
      this.isSuperAdmin
      ? {
        icon: clipboardUsersIcon,
        label: 'Communication Center',
        route: communicationCenterRoute,
        isHome: false,
        dropdown: [
          { label: 'Communications', route: communicationCenterRoute },
          { label: 'Communication Templates', route: 'communications-center/templates' }
        ]
        }
      : {
        icon: clipboardUsersIcon,
        label: 'Communication Center',
        route: communicationCenterRoute,
        isHome: false
        },
      {
      icon: clipboardUsersIcon,
      label: 'Rosters',
      route: 'rosters',
      isHome: false
      },
      ...(this.isSuperAdmin
      ? [
        {
          icon: grid2Icon,
          label: 'Program Management',
          route: 'program-management',
          dropdown: [
          { label: 'Award History', route: 'award-history' },
          { label: 'Award Management', route: 'award-management' },
          { label: 'Award Simulator', route: 'award-simulator' },
          { label: 'Enrollment Programs', route: 'view-programs' },
          { label: 'Wallet Files', route: 'classwallet-files' },
          { label: 'DNPE List', route: 'dnpe-import' },
          { label: 'Manage Institutions', route: 'institutions' },
          { label: 'PandaDoc Merge Fields', route: 'merge-fields' },
          { label: 'Program Settings', route: 'program-settings' },
          { label: 'Run Lottery', route: 'lottery' },
          { label: 'DAT Files History', route: 'dat-files-history' }
          ],
          isHome: false
        }
        ]
      : [])
    ];
  }

  private getNotifications(): void {
    this.notificationService.getNotifications(this.adminUrl).subscribe({
      error: (err: HttpErrorResponse) => this.appInsightsService.log(new Error(`Failed to load notifications: ${err.status}`))
    });
  }

  private getServices(): void {
    this.providerRecordService.getSubServices(2).subscribe({
      next: (res) => {
        this.providerRecordService.subServices = res;
      },
      error: (err: HttpErrorResponse) => this.appInsightsService.log(new Error(`Failed to load sub services: ${err.status}`))
    });
  }

  private loadStore(): void {
    forkJoin({
      academicYears: this.awardsService.getAcademicYears(),
      userGroups: from(this.authService.getUserGroups())
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ academicYears, userGroups }) => {
          this.adminStore.init(academicYears, userGroups);
        },
        error: (err: HttpErrorResponse) => {
          this.appInsightsService.log(new Error(`Failed to load academic years or user groups: ${err.status}`));
        }
      });
  }

  private async initAdmin(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount();
    this.systemService.userId = account?.localAccountId || '';
    this.userName = account?.name || '';
    await this.checkSuperadmin();
    this.buildHeader();
    this.getNotifications();
    this.getServices();
    this.loadStore();
    this.updateUnreadMessageCount();
    this.signalRService.startConnection(resolveSignalRBase(environment), ['AdminEvents', 'TaskStatus']);

    this.signalRService
      .getEventObservable<unknown>('TaskStatusUpdate')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (taskStatus) => {
          console.log('Task Status Update:', taskStatus);
          // Handle task status update here
        },
        error: () => this.appInsightsService.logEvent('AppComponent.TaskStatusUpdate.StreamError')
      });

    this.notificationService.refreshNotifications.pipe(takeUntil(this.destroy$)).subscribe({
      // Internal Subject trigger; the delegated call (getNotifications) has its own error handling.
      next: () => this.getNotifications()
    });
  }

  private updateUnreadMessageCount(): void {
    this.messageCenterService.getProvidersAndTopicsList().subscribe({
      next: (res) => {
        this.systemService.unreadMessageCount =
          res.topics.filter((x) => (x.unreadCount ?? 0) > 0 && x.assignedToId == this.systemService.userId).length ?? 0;
      },
      error: (err: HttpErrorResponse) =>
        this.appInsightsService.log(new Error(`Failed to load providers and topics list: ${err.status}`))
    });
  }

  private async checkSuperadmin(): Promise<void> {
    try {
      this.isSuperAdmin = await this.authService.userHasGroup('K12-admin-Global-admin');
    } finally {
      this.buildNavigation();
      this.showLoadingSpinner.set(false);
    }
  }

  private applyChromeForUrl(url: string): void {
    this.isFullPageRoute.set(isFullPageRoute(url));
    if (isFullPageRoute(url)) {
      this.showLoadingSpinner.set(false);
    }
  }
}
